import i18n from "i18next";
// Spec §4, §9, §11 — the "visibility strip" is one canonical string
// describing a doc's current sharing state. It appears in three
// places: the Share drawer header, the topbar tooltip on the
// avatar stack, and the mobile Panels Share row subtitle. They MUST
// agree, so all three derive from this selector.
//
// Format: "N people · {team or no team} · link {on/off}".
// People excludes the owner (the owner row in the drawer is
// implicit; we're describing additional ways in, not total seats).
// Team segment becomes "1 team" / "Optimization Lab" etc. The
// spec's mock alternates between "1 team" (compact) and the team
// name; we render the name when we have it (more meaningful), and
// fall back to "1 team" otherwise.

import type { UserCollaborator } from "@/lib/api";

export interface VisibilityInput {
  collaborators?: UserCollaborator[];
  ownerId?: string;
  teamName?: string | null;
  teamId?: string | null;
  isPublic?: boolean;
}

export interface VisibilityState {
  /** People-segment count. Excludes owner. */
  peopleCount: number;
  /** Always present; null when no team attached. */
  team: string | null;
  /** Link state. */
  linkOn: boolean;
  /** "Solo" = no people, no team, no link. */
  solo: boolean;
}

export function getVisibilityState(input: VisibilityInput): VisibilityState {
  const collaborators = input.collaborators ?? [];
  const peopleCount = collaborators.filter(
    (c) => !input.ownerId || c.userId !== input.ownerId,
  ).length;
  const team = input.teamId ? input.teamName ?? i18n.t("sharing:strip.oneTeam") : null;
  const linkOn = !!input.isPublic;
  return {
    peopleCount,
    team,
    linkOn,
    solo: peopleCount === 0 && !team && !linkOn,
  };
}

/** Compact one-liner used in headers + tooltips + mobile subtitles. */
export function visibilityStrip(input: VisibilityInput): string {
  const s = getVisibilityState(input);
  if (s.solo) return i18n.t("sharing:strip.justYou");
  const parts: string[] = [];
  if (s.peopleCount > 0) {
    parts.push(i18n.t("sharing:summary.people", { count: s.peopleCount }));
  }
  if (s.team) parts.push(s.team);
  parts.push(i18n.t(s.linkOn ? "sharing:strip.linkOn" : "sharing:strip.linkOff"));
  return parts.join(" · ");
}
